import { useCallback } from 'react';

import { trackEvent } from '@zep/logger/tracking';

import {
  type AiBuilderChatState,
  type AiBuilderServerSourceRegistrationRequest,
} from '../store/types';
import { isAiBuilderAuthExpiredError } from '../utils/aiBuilderAuthExpiry';
import { aiBuilderChatAdapter } from '../utils/aiBuilderChatAdapter';
import { type AiBuilderChatCopy } from '../utils/aiBuilderChatCopy';
import { createServerSourceRegistrationRequest } from '../utils/aiBuilderServerSources';

import { type useActiveSourceRefs } from './useActiveSourceRefs';

type UseAiBuilderRegisterServerSourceParams = {
  activeSourceRefs: ReturnType<typeof useActiveSourceRefs>;
  appendSystemErrorMessage: (content: string) => void;
  copy: AiBuilderChatCopy;
  draftInstanceId: string;
  getChatState: () => AiBuilderChatState;
  isTurnBusy: () => boolean;
  /** 로그인 만료 단일 처리기 — 스냅샷 저장 + 재로그인 모달. */
  onAuthExpired: () => void;
  updateState: (
    updater: (previous: AiBuilderChatState) => AiBuilderChatState,
  ) => void;
};

/**
 * URL·파일 자료를 현재 서버 세션에 등록한다.
 *
 * 등록은 턴 스트림(SSE)이 아니라 JSON 요청이라, 만료 401 이 `authExpired` 이벤트가
 * 아니라 예외로 온다. 스트림 catch 의 만료 분기가 닿지 않으므로 여기서 직접 가른다.
 */
export const useAiBuilderRegisterServerSource = ({
  activeSourceRefs,
  appendSystemErrorMessage,
  copy,
  draftInstanceId,
  getChatState,
  isTurnBusy,
  onAuthExpired,
  updateState,
}: UseAiBuilderRegisterServerSourceParams) =>
  useCallback(
    (request: AiBuilderServerSourceRegistrationRequest) => {
      if (isTurnBusy()) return;

      const sessionId = getChatState().sessionId;
      if (!sessionId) {
        appendSystemErrorMessage(copy.system.sourceRegistrationSessionRequired);
        return;
      }

      const sourceRequest = createServerSourceRegistrationRequest({
        draftInstanceId,
        request,
        sessionId,
      });

      void aiBuilderChatAdapter
        .registerSource(sourceRequest)
        .then(result => {
          if (getChatState().sessionId !== sessionId) return;

          if (result.status !== 'ready') {
            trackEvent('quiz_ai_builder_source_attach', {
              source_type: request.sourceType,
              status: 'error',
              session_id: sessionId,
            });
            appendSystemErrorMessage(copy.system.sourceRegistrationFailed);
            return;
          }

          activeSourceRefs.markActive(result.sourceRef);
          activeSourceRefs.recordSourceType(
            result.sourceRef,
            request.sourceType,
          );
          trackEvent('quiz_ai_builder_source_attach', {
            source_type: request.sourceType,
            status: 'ready',
            session_id: sessionId,
            ...(request.sourceType === 'file' && request.pageRanges
              ? {
                  selected_page_count: request.pageRanges.reduce(
                    (count, range) => count + range.end - range.start + 1,
                    0,
                  ),
                  range_count: request.pageRanges.length,
                }
              : {}),
          });
          updateState(prev => {
            if (prev.sourceRefs.includes(result.sourceRef)) return prev;

            return {
              ...prev,
              sourceRefs: [...prev.sourceRefs, result.sourceRef],
            };
          });
        })
        .catch((error: unknown) => {
          if (getChatState().sessionId !== sessionId) return;

          trackEvent('quiz_ai_builder_source_attach', {
            source_type: request.sourceType,
            status: 'error',
            session_id: sessionId,
          });

          // 만료는 재로그인 모달이 안내를 담당한다 — 일반 실패 말풍선으로 뭉개지 않는다.
          if (isAiBuilderAuthExpiredError(error)) {
            onAuthExpired();
            return;
          }

          appendSystemErrorMessage(copy.system.sourceRegistrationFailed);
        });
    },
    [
      activeSourceRefs,
      appendSystemErrorMessage,
      copy,
      draftInstanceId,
      getChatState,
      isTurnBusy,
      onAuthExpired,
      updateState,
    ],
  );
